import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MessageCircle, Send, User, Search, Lock, 
  ArrowLeft, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const navigate = useNavigate();
  const { recipientId } = useParams();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const hasMessaging = user?.subscription_tier === 'creator' || user?.subscription_tier === 'model';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!hasMessaging) {
      return;
    }
    fetchConversations();
  }, [isAuthenticated, hasMessaging]);

  useEffect(() => {
    if (recipientId && hasMessaging) {
      fetchMessages(recipientId);
    }
  }, [recipientId, hasMessaging]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, getAuthHeader());
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${userId}`, getAuthHeader());
      setMessages(response.data.messages || []);
      
      // Find user info from conversations
      const conv = conversations.find(c => c.other_user?.id === userId);
      if (conv) {
        setSelectedUser(conv.other_user);
      }
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !recipientId) return;

    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        recipient_id: recipientId,
        content: newMessage
      }, getAuthHeader());

      setMessages([...messages, {
        id: Date.now().toString(),
        sender_id: user.id,
        content: newMessage,
        created_at: new Date().toISOString()
      }]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedUser(conv.other_user);
    navigate(`/messages/${conv.other_user.id}`);
  };

  // Premium gate
  if (!hasMessaging) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="messages-locked">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gray-500" />
          </div>
          <h2 
            className="text-3xl font-normal tracking-wide uppercase mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Premium Feature
          </h2>
          <p className="text-gray-400 mb-6">
            Direct messaging is available for Creator and Model subscribers. Upgrade to connect with other creators.
          </p>
          <Button 
            onClick={() => navigate('/subscription')}
            className="bg-white text-black hover:bg-gray-200"
            data-testid="upgrade-btn"
          >
            Upgrade Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="messages-page">
      <div className="h-[calc(100vh-80px)] flex">
        {/* Conversations Sidebar */}
        <div className="w-80 border-r border-white/10 flex flex-col bg-[#0A0A0A]">
          <div className="p-4 border-b border-white/10">
            <h2 
              className="text-xl font-normal tracking-wide uppercase mb-4"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Messages
            </h2>
            <div className="relative">
              <Input
                placeholder="Search conversations..."
                className="bg-[#1A1A1A] border-white/10 pl-10"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 px-4">
                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No conversations yet</p>
                <p className="text-gray-600 text-xs mt-1">Start chatting with other creators</p>
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors text-left ${
                    recipientId === conv.other_user?.id ? 'bg-white/10' : ''
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {conv.other_user?.username || 'Unknown'}
                    </p>
                    <p className="text-gray-500 text-xs truncate">
                      Click to view messages
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-[#050505]">
          {recipientId ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-white/10 flex items-center gap-3">
                <button 
                  onClick={() => navigate('/messages')}
                  className="md:hidden p-2 hover:bg-white/10 rounded"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">{selectedUser?.username || 'User'}</p>
                  <p className="text-gray-500 text-xs capitalize">{selectedUser?.subscription_tier || 'Member'}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-2 rounded-lg ${
                        msg.sender_id === user.id
                          ? 'bg-white text-black'
                          : 'bg-[#1A1A1A] text-white'
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.sender_id === user.id ? 'text-gray-600' : 'text-gray-500'}`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSend} className="p-4 border-t border-white/10">
                <div className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-[#1A1A1A] border-white/10"
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || !newMessage.trim()}
                    className="bg-white text-black hover:bg-gray-200"
                    data-testid="send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Select a conversation to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
